/*
 * Image
 *
 * Supports:
 * - Retina
 * - Loading thumbnail
 * - Lazy loading of full resolution src
 * - Webp src for suitable browsers
 */

import React, { useRef } from 'react';

import { useLazyImage } from '$hooks/images';

const emptySrc = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/media.d.ts').Image}
 */
const Image = ({
    alt,
    className,
    containerClassName,
    containerStyle,
    height,
    loading: loadingSrc,
    loadingStyle,
    psrc,
    px2,
    role,
    src,
    style,
    width,
    x2
}) => {
    const imageRef = useRef();

    const {
        imageSrc,
        imageX2,
        loaded
    } = useLazyImage({
        imageRef,
        src,
        psrc,
        x2,
        px2
    });

    return (
        <div
            className={containerClassName}
            style={{
                ...{
                    height: 0,
                    overflow: 'hidden',
                    paddingBottom: width && height ? `${(height / width) * 100}%` : 'initial',
                    position: 'relative',
                    width: '100%'
                },
                ...containerStyle
            }}>
            <div style={{
                bottom: 0,
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0
            }}>
                {loadingSrc && (
                    <div
                        className='pixelated'
                        style={{
                            ...{
                                backgroundImage: `url(${loadingSrc})`,
                                opacity: loaded ? 0 : 1,
                                height: '100%',
                                position: 'absolute',
                                transition: 'opacity 2s',
                                width: '100%',
                            },
                            ...loadingStyle
                        }} />
                )}

                <img
                    alt={alt}
                    className={className}
                    height={height}
                    ref={imageRef}
                    role={role}
                    src={imageSrc || emptySrc}
                    srcSet={imageX2 ? `${imageX2} 2x` : null}
                    style={{
                        ...{
                            opacity: loaded ? 1 : 0,
                            position: 'absolute',
                            top: 0,
                            transition: 'opacity .3s',
                            width: '100%'
                        },
                        ...style
                    }}
                    width={width} />
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default Image;
